<template>
	<div class="apps">
		<div class="apps__header">
			<h2 class="apps__title">Connected Apps</h2>
			<p class="apps__description">
				Review connected OAuth clients, inspect connected devices, and revoke access instantly.
			</p>
		</div>

		<HeaderLoader :is-loading="loading" />

		<Message
			v-if="errorMessage"
			severity="error"
			:closable="false">
			{{ errorMessage }}
		</Message>

		<DataTable
			class="apps__table"
			responsive-layout="scroll"
			:value="connections">
			<template #empty>
				<div class="apps__empty">No connected apps found.</div>
			</template>
			<Column header="Client">
				<template #body="{ data }">
					<div class="apps__client-cell">
						<span class="apps__client-name">{{ data.client_name }}</span>
					</div>
				</template>
			</Column>
			<Column header="Authorized">
				<template #body="{ data }">
					{{ formatDate(getAuthorizedAt(data)) }}
				</template>
			</Column>
			<Column header="Devices">
				<template #body="{ data }">
					{{ data.tokens.length }}
				</template>
			</Column>
			<Column header-style="width: 96px">
				<template #body="{ data }">
					<div class="apps__actions">
						<DataTableButton
							aria-label="Devices"
							icon="fal fa-laptop-mobile"
							title="Devices"
							@click="openDevicesDialog(data)" />
						<DataTableButton
							aria-label="Disconnect"
							icon="fal fa-link-slash"
							title="Disconnect"
							:disabled="disconnectingClientIds.has(data.client_id)"
							:loading="disconnectingClientIds.has(data.client_id)"
							@click="confirmDisconnect(data)" />
					</div>
				</template>
			</Column>
		</DataTable>

		<AppsDevicesDialog
			v-model:visible="showDevicesDialog"
			:header="devicesDialogTitle"
			:revoking-token-ids="revokingTokenIds"
			:tokens="selectedConnection?.tokens || []"
			@revoke="revokeToken" />
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { format } from 'date-fns'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import Message from 'primevue/message'
import { useConfirm } from 'primevue/useconfirm'
import HeaderLoader from '@/components/HeaderLoader.vue'
import DataTableButton from '@/components/Table/DataTableButton.vue'
import AppsDevicesDialog from '@/views/User/components/AppsDevicesDialog.vue'
import McpApi from '@/models/Mcp/Api'
import type { McpConnection } from '@/models/Mcp/Types'

const confirm = useConfirm()
const api = new McpApi()

const loading = ref(false)
const errorMessage = ref<string | null>(null)
const connections = ref<McpConnection[]>([])
const selectedConnection = ref<McpConnection | null>(null)
const showDevicesDialog = ref(false)
const disconnectingClientIds = reactive(new Set<string>())
const revokingTokenIds = reactive(new Set<string>())

const devicesDialogTitle = computed(() => {
	if (!selectedConnection.value) return 'Connected Devices'

	return `${selectedConnection.value.client_name} Devices`
})

onMounted(async () => {
	await fetchConnections()
})

async function fetchConnections() {
	loading.value = true
	errorMessage.value = null

	try {
		const response = await api.listConnections()
		connections.value = response.data.data

		if (selectedConnection.value) {
			selectedConnection.value =
				connections.value.find(
					(connection) => connection.client_id === selectedConnection.value?.client_id,
				) || null
		}
	} catch (e: any) {
		errorMessage.value = e.response?.data?.message || 'Failed to fetch connected apps'
	} finally {
		loading.value = false
	}
}

function openDevicesDialog(connection: McpConnection) {
	selectedConnection.value = connection
	showDevicesDialog.value = true
}

function confirmDisconnect(connection: McpConnection) {
	confirm.require({
		message: `Disconnect ${connection.client_name}? All connected devices will be revoked.`,
		header: 'Disconnect App',
		icon: 'fas fa-exclamation-triangle',
		rejectLabel: 'Cancel',
		rejectProps: {
			label: 'Cancel',
			severity: 'secondary',
			outlined: true,
		},
		acceptProps: {
			label: 'Disconnect',
			severity: 'danger',
		},
		accept: async () => {
			await disconnectConnection(connection)
		},
		reject: () => {},
	})
}

async function disconnectConnection(connection: McpConnection) {
	if (disconnectingClientIds.has(connection.client_id)) return

	disconnectingClientIds.add(connection.client_id)
	errorMessage.value = null

	try {
		await api.disconnectConnection(connection.client_id)
		await fetchConnections()

		if (selectedConnection.value?.client_id === connection.client_id) {
			showDevicesDialog.value = false
		}
	} catch (e: any) {
		errorMessage.value = e.response?.data?.message || 'Failed to disconnect app'
	} finally {
		disconnectingClientIds.delete(connection.client_id)
	}
}

async function revokeToken(tokenId: string) {
	if (!selectedConnection.value || revokingTokenIds.has(tokenId)) return

	revokingTokenIds.add(tokenId)
	errorMessage.value = null

	try {
		await api.revokeConnectionToken(selectedConnection.value.client_id, tokenId)
		await fetchConnections()

		if (!selectedConnection.value) {
			showDevicesDialog.value = false
		}
	} catch (e: any) {
		errorMessage.value = e.response?.data?.message || 'Failed to revoke device token'
	} finally {
		revokingTokenIds.delete(tokenId)
	}
}

function getAuthorizedAt(connection: McpConnection): string | null {
	if (!connection.tokens.length) return null

	// API returns tokens ordered by created_at desc; oldest token is last.
	return connection.tokens[connection.tokens.length - 1]?.created_at ?? null
}

function formatDate(value: string | null): string {
	if (!value) return '-'

	return format(new Date(value), 'MMM d, yyyy')
}
</script>

<style scoped lang="scss">
.apps {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 24px;

	.apps__header {
		display: flex;
		flex-direction: column;
		gap: 8px;

		.apps__title {
			font-size: 24px;
			font-weight: 800;
			margin: 0;
		}

		.apps__description {
			color: var(--p-text-muted-color);
			margin: 0;
			line-height: 1.5;
		}
	}

	.apps__table {
		border-radius: 8px;
		overflow: hidden;
	}

	.apps__client-cell {
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.apps__client-name {
		font-weight: 600;
	}

	.apps__actions {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
	}

	.apps__empty {
		text-align: center;
		padding: 32px;
		color: var(--p-text-muted-color);
	}
}
</style>
